import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useWebRTC } from '@/hooks/useWebRTC.tsx';
import { SignalingMessageType, Signals, useSignaling } from './SignalingContext.tsx';
import { useMediaDevices } from './MediaDevicesContext.tsx';
import { useCountryDetection } from '@/hooks/useCountryDetection.tsx';
import { CountryInfo } from '@/services/geolocation';
import { AppError, ErrorCode } from '@/types/errors.ts';

interface WebRTCContextType {
  remoteStream: MediaStream | null;
  rtcConnectionState?: 'connected' | 'connecting' | 'disconnected' | 'new';
  signalingState: {
    prev?: RTCSignalingState;
    curr: RTCSignalingState;
  };
  error: AppError | null;
  setIceConfiguration: React.Dispatch<React.SetStateAction<RTCConfiguration | null>>;
  createDataChannel: (label: string, handlers: {
    onOpen: (dataChannel: RTCDataChannel, event: Event) => any;
    onMessage?: (event: MessageEvent) => any;
    onClose?: (event: Event) => any;
  }, options?: RTCDataChannelInit) => void;
  handleRetryRtcConnection: () => void;
  addLocalTracks: (stream: MediaStream) => void;
  resetTrigger: boolean;
  setResetTrigger: React.Dispatch<React.SetStateAction<boolean>>;
  generalDataChannel: RTCDataChannel | undefined;
  peerVideoEnabled: boolean;
  peerAudioEnabled: boolean;
  peerName: string | null;
  peerFilter: string;
  peerCountry: CountryInfo | null;
  userCountry: CountryInfo | null;
  // Local controls for peer media (client-side only)
  isPeerAudioMuted: boolean;
  isPeerVideoHidden: boolean;
  togglePeerAudioMute: () => void;
  togglePeerVideoHide: () => void;
}

interface GeneralDataChannelMessage {
  type: 'video-toggle' | 'audio-toggle' | 'name-exchange' | 'filter-change' | 'country-exchange';
  payload: any;
}

const WebRTCContext = createContext<WebRTCContextType | null>(null);

const MAX_RETRIES = 5;

export function WebRTCProvider({ children }: { children: React.ReactNode }) {
  // states //
  const [ iceConfiguration, setIceConfiguration ] = useState<RTCConfiguration | null>(null);
  // if the peer context is set reset, this resetTrigger is pulled. So the dependents listening to this event can act accordingly
  const [ resetTrigger, setResetTrigger ] = useState(false);
  const [ error, setError ] = useState<AppError | null>(null);
  const [ waitingForResetCompletion, setWaitingForResetCompletion ] = useState(false);
  const retryAttemptRef = useRef(0);
  const [ generalDataChannel, setGeneralDataChannel ] = useState<RTCDataChannel | undefined>(undefined);
  const [ peerVideoEnabled, setPeerVideoEnabled ] = useState(false);
  const [ peerAudioEnabled, setPeerAudioEnabled ] = useState(false);
  const [ peerName, setPeerName ] = useState<string | null>(null);
  const [ peerFilter, setPeerFilter ] = useState('none');
  const [ peerCountry, setPeerCountry ] = useState<CountryInfo | null>(null);
  // Local peer media controls (client-side only)
  const [ isPeerAudioMuted, setIsPeerAudioMuted ] = useState(false);
  const [ isPeerVideoHidden, setIsPeerVideoHidden ] = useState(false);
  // states //

  // other contexts
  const { localStream } = useMediaDevices();
  const { sendSignal, addSignalingListener, removeSignalingListener } = useSignaling();
  const { userCountry } = useCountryDetection();
  // other contexts

  const onCandidateDiscovery = (candidate: RTCIceCandidate) => {
    sendSignal({ signal: Signals.IceCandidate, candidate });
  };

  const onOfferSet = (description: RTCSessionDescription | null) => {
    if (!description) {
      setError( { code: ErrorCode.LocalDescriptionFailed1 });
      return;
    }
    console.debug('Offer sent down the server');
    sendSignal({ signal: Signals.Offer, description });
  };

  const onAnswerSet = (description: RTCSessionDescription | null) => {
    if (!description) {
      setError( { code: ErrorCode.LocalDescriptionFailed2 });
      return;
    }
    console.debug('Answer sent down the server');
    sendSignal({ signal: Signals.Answer, description });
  };

  const {
    remoteStream,
    rtcConnectionState,
    signalingState,
    error: rtcConnectionError,
    setPolite,
    addLocalTracks,
    reset,
    handleSignal,
    createDataChannel
  } = useWebRTC({
    iceConfiguration,
    onCandidateDiscovery,
    onOfferSet,
    onAnswerSet
  });

  const rtcConnectionMessageHandler = useCallback(async (event: MessageEvent<any>) => { // todo:
    const message = JSON.parse(event.data) as SignalingMessageType;
    const { signal } = message;
    switch (signal) {
      case Signals.BePolite:
        console.log('Be polite received');
        setPolite();
        break;
      case Signals.Close:
        console.log('Close received');
        console.log('Trigger resetting rtc connection');
        console.log(message);
        if (message.error) { setError(message.error); }
        setResetTrigger(true);
        break;
      case Signals.Offer:
      case Signals.Answer:
      case Signals.IceCandidate:
        await handleSignal(message);
        break;
    }
  }, [ localStream, handleSignal ]);

  const handleRetryRtcConnection = () => {
    console.log('Retrying rtc connection initialization');
    // Clear error and reset retry counter for fresh attempt
    setError(null);
    retryAttemptRef.current = 0;
    if (iceConfiguration) {
      reset();
    }
  };

  // Local peer media control functions
  const togglePeerAudioMute = useCallback(() => {
    setIsPeerAudioMuted(prev => !prev);
  }, []);

  const togglePeerVideoHide = useCallback(() => {
    setIsPeerVideoHidden(prev => !prev);
  }, []);

  // effects
  // set rtc errors to this errors
  useEffect(() => {
    if (!error && rtcConnectionError) setError(rtcConnectionError);
  }, [error, rtcConnectionError]);

  useEffect(() => {
    const resetRtcCallback = () => { // todo: move this to signaling context
      setResetTrigger(true);
      // console.log('Trigger resetting rtc connection');
      // reset();
    };
    addSignalingListener('message', rtcConnectionMessageHandler);
    addSignalingListener('close', resetRtcCallback);

    return () => {
      removeSignalingListener('close', resetRtcCallback);
      removeSignalingListener('message', rtcConnectionMessageHandler);
    };
  }, [ rtcConnectionMessageHandler, addSignalingListener, removeSignalingListener ]);

  // to switch off the pulled trigger
  useEffect(() => {
    if (resetTrigger) {
      console.log('Resetting rtc connection');
      generalDataChannel?.close();
      reset();
      setWaitingForResetCompletion(true);
      setResetTrigger(false);
      setGeneralDataChannel(undefined);

      // Send Closed acknowledgement to backend to confirm reset completion
      // This allows backend to transition out of clearing state immediately
      // instead of waiting for 10s timeout
      sendSignal({ signal: Signals.Closed });
    }
  }, [ resetTrigger, sendSignal ]);

  useEffect(() => {
    if (!waitingForResetCompletion) return;
    // console.log('Waiting for reset completion', rtcConnectionState);
    if (rtcConnectionState === 'new') {
      sendSignal({ signal: Signals.Closed });
      console.log('Reset completed. Signaling closed');
      setWaitingForResetCompletion(false);
    }
  }, [ rtcConnectionState, waitingForResetCompletion ]);

  useEffect(() => {
    if (generalDataChannel) return;

    const label = 'general';
    createDataChannel(label, {
      onMessage: (event) => {
        const message = JSON.parse(event.data) as GeneralDataChannelMessage;
        const { type } = message;
        switch (type) {
          case 'video-toggle':
            setPeerVideoEnabled(message.payload === 'on');
            break;
          case 'audio-toggle':
            setPeerAudioEnabled(message.payload === 'on');
            break;
          case 'name-exchange':
            setPeerName(message.payload);
            break;
          case 'filter-change':
            setPeerFilter(message.payload);
            break;
          case 'country-exchange':
            setPeerCountry(message.payload);
            break;
          default:
            console.warn(`Unknown general data channel message type: ${type}`);
            break;
        }
      },
      onOpen: (dataChannel) => {
        setGeneralDataChannel(dataChannel);
        // Send user's country information immediately when channel opens
        if (userCountry) {
          const countryMessage: GeneralDataChannelMessage = {
            type: 'country-exchange',
            payload: userCountry
          };
          dataChannel.send(JSON.stringify(countryMessage));
        }
      },
      onClose: () => {
        console.log('General channel closed');
      }
    }, { negotiated: true, id: 100 });
  }, [ generalDataChannel ]);

  useEffect(() => {
    if (!remoteStream) return;
    setPeerVideoEnabled(remoteStream.getVideoTracks().some(track => track.enabled));
    setPeerAudioEnabled(remoteStream.getAudioTracks().some(track => track.enabled));
  }, [ remoteStream ]);

  // Reset retry counter when RTC connection succeeds
  useEffect(() => {
    if (rtcConnectionState === 'connected') {
      retryAttemptRef.current = 0;
    }
  }, [ rtcConnectionState ]);

  // Reset local peer controls when peer changes or session resets
  useEffect(() => {
    if (resetTrigger) {
      setIsPeerAudioMuted(false);
      setIsPeerVideoHidden(false);
      setPeerCountry(null);
      // Reset retry counter on new peer connection
      retryAttemptRef.current = 0;
    }
  }, [ resetTrigger ]);

  // Send country information when it becomes available and data channel is ready
  useEffect(() => {
    if (generalDataChannel && userCountry && generalDataChannel.readyState === 'open') {
      const countryMessage: GeneralDataChannelMessage = {
        type: 'country-exchange',
        payload: userCountry
      };
      generalDataChannel.send(JSON.stringify(countryMessage));
    }
  }, [ generalDataChannel, userCountry ]);

  return <WebRTCContext.Provider value={{
    remoteStream,
    rtcConnectionState,
    signalingState,
    error,
    setIceConfiguration,
    createDataChannel,
    handleRetryRtcConnection,
    addLocalTracks,
    resetTrigger,
    setResetTrigger,
    generalDataChannel,
    peerVideoEnabled,
    peerAudioEnabled,
    peerName,
    peerFilter,
    peerCountry,
    userCountry,
    isPeerAudioMuted,
    isPeerVideoHidden,
    togglePeerAudioMute,
    togglePeerVideoHide
  }}>{children}</WebRTCContext.Provider>;
}

export function usePeer() {
  const context = useContext(WebRTCContext);
  if (!context) {
    throw new Error('usePeer must be used within a WebRTCProvider');
  }
  return context;
}